import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import { makeStyles, ThemeProvider } from "@material-ui/core/styles"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import { green, red } from "@material-ui/core/colors"
import { Button, FormControl, CircularProgress } from "@material-ui/core"
import {
  getPutSignedUrl,
  putFileS3,
  parseToCSV,
  validateCSVAndImport,
} from "../api/apiCalls"
import { UserContext } from "../contexts/UserContext"
import { HistoryContext } from "../contexts/HistoryContext"
import { MessagingContext } from "../contexts/MessagingContext"
import theme from "../theme/mainTheme"

const useStyles = makeStyles((theme) => ({
  card: {
    "& > *": {
      margin: theme.spacing(1),
    },
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    spacing: 3,
    marginBottom: 50,
  },
  error: {
    color: red[500],
  },
  input: {
    display: "none",
  },
  detail: {
    fontSize: 12,
    fontStyle: "italic",
  },
  uploadBtn: {
    position: "absolute",
    right: 10,
    top: 10,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
  },
  button: {
    minWidth: 300,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  buttonProgress: {
    color: green,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  wrapper: {
    position: "relative",
  },
}))

const UploadFileButton = ({ label, disabled, context, callBack, inline = true }) => {
  const [transferring, setTransferring] = useState(false)
  const [inputKey, setInputKey] = useState("")
  const [error, setError] = useState()
  const [parsing, setParsing] = useState(false)
  const classes = useStyles()
  const { user } = useContext(UserContext)
  const { fetchHistory, saveActivity, saveError } = useContext(HistoryContext)
  const { addSuccessMessage, addErrorMessage } = useContext(MessagingContext)

  const changeInputKey = () => {
    let randomString = Math.random().toString(36)
    setInputKey(randomString)
  }

  const uploadFile = (event) => {
    setTransferring(true)
    getPutSignedUrl(user, context, event.target.files[0]).then(
      ({ url, key, file }) => {
        // add activity feeed
        if (label === "Estimate Naming Convention") {
          putFileS3(url, file, file.type)
            .then(async (response) => {
              setTransferring(false)
              changeInputKey()
              if (response.status === 200) {
                setParsing(true)
                parseToCSV(user, context, key.split("/")[2], label).then(
                  async (parsedResponse) => {
                    console.log("parseToCSV response: ", parsedResponse)
                    if (parsedResponse.statusCode === 200) {
                      let splitURL = url.split("?")[0].split("/")
                      console.log(" SPLIT URL ", splitURL)
                      console.log("parseToCSV passed status 200")
                      await new Promise((resolve) => setTimeout(resolve, 3000))
                      validateCSVAndImport(
                        user,
                        parsedResponse.filepath,
                        `${splitURL[3]}/${splitURL[4]}/${splitURL[5]}`
                      ).then(async (resp) => {
                        if (resp.Payload && resp.Payload.startsWith("INFO:  200")) {
                          setParsing(false)
                          await saveActivity(
                            user,
                            file.name,
                            true,
                            context,
                            key.split("/")[2]
                          )
                          fetchHistory(user)
                          //await add success message
                          addSuccessMessage("UPLOAD", `${file.name} Upload Complete`)
                        } else {
                          //addErrorMessage("ERROR was found with", resp.Payload)
                          setParsing(false)
                          await saveError(
                            user,
                            file.name,
                            false,
                            context,
                            true,
                            resp.Payload
                          )
                          fetchHistory(user)
                        }
                      })
                    } else {
                      addErrorMessage(
                        "ERROR",
                        `${response.status} ${response.statusText}`
                      )
                      setParsing(false)
                      await saveError(user, file.name, false, context, true)
                      fetchHistory(user)
                    }
                  }
                )
              } else {
                addErrorMessage("ERROR", `${response.status} ${response.statusText}`)
                await saveError(user, file.name, false, context, true)
                fetchHistory(user)
              }

              if (callBack) {
                callBack()
              }
            })
            .catch((error) => {
              setError(error)
            })
        } else {
          putFileS3(url, file, file.type)
            .then(async (response) => {
              setTransferring(false)
              changeInputKey()
              if (response.status === 200) {
                await saveActivity(user, file.name, true, context, key.split("/")[2])
                fetchHistory(user)
                setParsing(true)
                await parseToCSV(user, context, key.split("/")[2])
                setParsing(false)
                fetchHistory(user)
                //await add success message
                addSuccessMessage("UPLOAD", `${file.name} Upload Complete`)
              } else {
                addErrorMessage("ERROR", `${response.status} ${response.statusText}`)
                await saveError(user, file.name, false, context, true)
                fetchHistory(user)
              }

              if (callBack) {
                callBack()
              }
            })
            .catch((error) => {
              setError(error)
            })
        }
      }
    )
  }
  return (
    <ThemeProvider theme={theme}>
      <FormControl className={inline ? classes.formControl : ""}>
        <input
          key={inputKey}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          className={classes.input}
          id={`upload-${context}`}
          multiple
          disabled={disabled}
          onChange={(e) => {
            if (!disabled) {
              uploadFile(e)
            } else {
              e.preventDefault()
            }
          }}
          type="file"
        />
        <label htmlFor={`upload-${context}`}>
          <div className={classes.wrapper}>
            <Button
              className={classes.button}
              variant={transferring ? "outlined" : "contained"}
              color="primary"
              component={`spam${context}`}
              disableElevation
              disabled={transferring || disabled}
              startIcon={<CloudUploadIcon />}
            >
              {label}
            </Button>
            {transferring && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
            {error && <div className={classes.error}>{error}</div>}
            {parsing && <div className={classes.parsing}>parsing</div>}
          </div>
        </label>
      </FormControl>
    </ThemeProvider>
  )
}

UploadFileButton.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  context: PropTypes.string,
  callBack: PropTypes.func,
  inline: PropTypes.bool,
}

export default UploadFileButton
